/** Matches API `GeographyDetailApiModel` / `RegGeography` JSON (camelCase). */
export interface IGeographyDetail {
    id: string;
    code: string;
    geographyType: string;
    description: string;
    isActive: boolean;
    isDeleted: boolean;
    source: string;
    sourceVersion: string;
    effectiveFromUtc: string;
    createdAtUtc: string;
    updatedAtUtc: string;
    createdByDisplayName?: string | null;
    lastUpdatedByDisplayName?: string | null;
    deletedAuditAtUtc?: string | null;
    deletedByDisplayName?: string | null;
    disabledAuditAtUtc?: string | null;
    disabledByDisplayName?: string | null;
}

export const GEOGRAPHY_TYPES = ["REGION", "SUB-REGION", "DISTRICT"] as const;
export type GeographyType = (typeof GEOGRAPHY_TYPES)[number];

/** Display labels for create dropdown (values remain canonical enum strings). */
export const GEOGRAPHY_TYPE_LABELS: Record<GeographyType, string> = {
    REGION: "Region",
    "SUB-REGION": "Sub-Region",
    DISTRICT: "District",
};

export function formatGeographyTypeLabel(geographyType: string | null | undefined): string {
    if (!geographyType) return "";
    const normalized = geographyType.trim().toUpperCase() as GeographyType;
    return GEOGRAPHY_TYPE_LABELS[normalized] ?? geographyType;
}

export type GeographyCreatePayload = {
    code: string;
    geographyType: GeographyType | string;
    description: string;
    isActive?: boolean;
};

export type GeographyUpdatePayload = {
    id: string;
    code: string;
    description: string;
};

export type GeographyRestoreDeletedPayload = {
    id: string;
    description: string;
    isActive?: boolean;
};

/** Machine-readable create conflict codes (HTTP 409). */
export const GEOGRAPHY_CREATE_CONFLICT = {
    deletedRestoreAvailable: "geography.deletedRestoreAvailable",
    deletedTypeMismatch: "geography.deletedTypeMismatch",
    duplicateActive: "geography.duplicateActive",
} as const;

export type GeographyDeletedRestoreAvailableConflict = {
    code: typeof GEOGRAPHY_CREATE_CONFLICT.deletedRestoreAvailable;
    message: string;
    existingId: string;
    existingCode: string;
    geographyType: string;
};

export type GeographyDeletedTypeMismatchConflict = {
    code: typeof GEOGRAPHY_CREATE_CONFLICT.deletedTypeMismatch;
    message: string;
    existingId?: string;
    existingCode?: string;
    existingGeographyType: string;
    requestedGeographyType: string;
};

export type GeographyCreateConflict =
    | GeographyDeletedRestoreAvailableConflict
    | GeographyDeletedTypeMismatchConflict;

export function isGeographyDeletedRestoreAvailableConflict(
    value: unknown
): value is GeographyDeletedRestoreAvailableConflict {
    return (
        typeof value === "object" &&
        value !== null &&
        (value as GeographyDeletedRestoreAvailableConflict).code ===
            GEOGRAPHY_CREATE_CONFLICT.deletedRestoreAvailable &&
        typeof (value as GeographyDeletedRestoreAvailableConflict).existingId === "string"
    );
}

export function isGeographyDeletedTypeMismatchConflict(
    value: unknown
): value is GeographyDeletedTypeMismatchConflict {
    return (
        typeof value === "object" &&
        value !== null &&
        (value as GeographyDeletedTypeMismatchConflict).code ===
            GEOGRAPHY_CREATE_CONFLICT.deletedTypeMismatch
    );
}

/** Thrown by geographies service when create returns a structured 409 conflict. */
export class GeographyCreateConflictError extends Error {
    readonly conflict: GeographyCreateConflict;

    constructor(conflict: GeographyCreateConflict) {
        super(conflict.message);
        this.name = "GeographyCreateConflictError";
        this.conflict = conflict;
    }
}
